import React, { useMemo, useState } from "react";
import {
    Activity,
    BookOpen,
    Calculator,
    FileText,
    Layers,
    MessageSquare,
    Mic,
    Palette,
    Phone,
    Rocket,
    Scale,
    Shield,
    Sparkles,
    ToggleLeft,
    ToggleRight,
    TrendingUp,
    Zap,
} from "lucide-react";

const IKON_MAP = {
    rocket: Rocket,
    message: MessageSquare,
    calculator: Calculator,
    shield: Shield,
    scale: Scale,
    chart: TrendingUp,
    file: FileText,
    book: BookOpen,
    sparkles: Sparkles,
    zap: Zap,
    mic: Mic,
    phone: Phone,
    palette: Palette,
    activity: Activity,
    layers: Layers,
};

const GRUP_SIRASI = ["temel", "cevap", "etkilesim", "tasarim", "sistem"];

const GRUP_ETIKET = {
    temel: "Temel",
    cevap: "Cevap Modülleri",
    etkilesim: "Etkileşim",
    tasarim: "Tasarım & Görünüm",
    sistem: "Sistem",
};

function ModulToggle({ aktif, onToggle }) {
    return (
        <button type="button" onClick={onToggle} className={aktif ? "text-emerald-400" : "text-slate-500"}>
            {aktif ? <ToggleRight className="h-8 w-8" /> : <ToggleLeft className="h-8 w-8" />}
        </button>
    );
}

export default function AkilWidgetModulMerkezi({ katalog, moduller, patch, onSelectModul, seciliModul }) {
    const gruplu = useMemo(() => {
        const g = {};
        (katalog || []).forEach((m) => {
            const grup = m.grup || "sistem";
            if (!g[grup]) g[grup] = [];
            g[grup].push(m);
        });
        return g;
    }, [katalog]);

    const aktifSayisi = useMemo(
        () => Object.values(moduller || {}).filter((m) => m && m.aktif !== false).length,
        [moduller]
    );

    return (
        <section className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#0B1220] to-[#121A2F] p-5 space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <span className="h-11 w-11 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/35 flex items-center justify-center">
                        <Layers className="h-5 w-5 text-[#D4AF37]" />
                    </span>
                    <div>
                        <h3 className="text-lg font-semibold text-white">Modül Merkezi</h3>
                        <p className="text-xs text-slate-400">
                            {aktifSayisi} modül aktif · Aç/kapa ile mbshukuk.com widget davranışını modüler yönetin
                        </p>
                    </div>
                </div>
            </div>

            {GRUP_SIRASI.map((grup) => {
                const liste = gruplu[grup];
                if (!liste?.length) return null;
                return (
                    <div key={grup} className="space-y-2">
                        <p className="text-[10px] uppercase tracking-widest text-slate-500">{GRUP_ETIKET[grup] || grup}</p>
                        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-2">
                            {liste.map((meta) => {
                                const mid = meta.id;
                                const state = moduller?.[mid] || { aktif: meta.varsayilan_aktif !== false };
                                const aktif = state.aktif !== false;
                                const Icon = IKON_MAP[meta.ikon] || Layers;
                                const secili = seciliModul === mid;
                                return (
                                    <div
                                        key={mid}
                                        className={`rounded-xl border p-3 transition-all cursor-pointer ${
                                            secili
                                                ? "border-[#D4AF37]/50 bg-[#D4AF37]/10"
                                                : aktif
                                                  ? "border-emerald-500/25 bg-emerald-500/5 hover:border-emerald-500/40"
                                                  : "border-white/10 bg-black/20 opacity-75 hover:border-white/20"
                                        }`}
                                        onClick={() => onSelectModul(mid)}
                                        onKeyDown={(e) => e.key === "Enter" && onSelectModul(mid)}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex items-start gap-2 min-w-0">
                                                <Icon className={`h-4 w-4 shrink-0 mt-0.5 ${aktif ? "text-emerald-400" : "text-slate-500"}`} />
                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium text-white truncate">{meta.label}</p>
                                                    <p className="text-[11px] text-slate-500 line-clamp-2">{meta.aciklama}</p>
                                                </div>
                                            </div>
                                            <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                                                <ModulToggle
                                                    aktif={aktif}
                                                    onToggle={() => patch(`moduller.${mid}.aktif`, !aktif)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}

            {seciliModul && (katalog || []).find((m) => m.id === seciliModul)?.alt_ayarlar?.length ? (
                <div className="rounded-xl border border-white/10 bg-black/30 p-4 space-y-3">
                    <p className="text-sm font-medium text-white">
                        {(katalog || []).find((m) => m.id === seciliModul)?.label} — alt ayarlar
                    </p>
                    {(katalog || [])
                        .find((m) => m.id === seciliModul)
                        ?.alt_ayarlar.map((alt) => {
                            const val = moduller?.[seciliModul]?.[alt.key];
                            if (alt.tip === "bool") {
                                return (
                                    <div key={alt.key} className="flex items-center justify-between text-sm">
                                        <span className="text-slate-300">{alt.label}</span>
                                        <ModulToggle
                                            aktif={val !== false}
                                            onToggle={() => patch(`moduller.${seciliModul}.${alt.key}`, val === false)}
                                        />
                                    </div>
                                );
                            }
                            if (alt.tip === "list") {
                                return (
                                    <label key={alt.key} className="block space-y-1">
                                        <span className="text-[11px] text-slate-500">{alt.label}</span>
                                        <textarea
                                            className="w-full rounded-lg border border-white/10 bg-[#0B1220] px-3 py-2 text-xs text-white min-h-[64px]"
                                            value={Array.isArray(val) ? val.join(", ") : (val || alt.varsayilan || []).join(", ")}
                                            onChange={(e) =>
                                                patch(
                                                    `moduller.${seciliModul}.${alt.key}`,
                                                    e.target.value.split(",").map((s) => s.trim()).filter(Boolean)
                                                )
                                            }
                                        />
                                    </label>
                                );
                            }
                            return null;
                        })}
                </div>
            ) : null}
        </section>
    );
}

export { GRUP_ETIKET, IKON_MAP };
